import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const Header = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    font-size: 1.5rem;
    color: #333;
    margin: 0;
  }

  .count {
    color: #666;
    font-size: 1rem;
  }
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:focus {
    border-color: #34574A;
    outline: none;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    border-color: #34574A;
    outline: none;
  }
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const PropertyCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
`;

const PropertyImage = styled.div`
  position: relative;
  padding-top: 66.67%; /* 3:2 Aspect Ratio */
  background: ${props => `url(${props.$image}) center/cover no-repeat`};
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$active ? '#ff0000' : 'white'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.$active ? '#ffffff' : '#666'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.1);
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const PropertyContent = styled.div`
  padding: 20px;
`;

const PropertyTitle = styled.h3`
  font-size: 1.125rem;
  color: #333;
  margin: 0 0 8px 0;
`;

const PropertyLocation = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PropertyDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;

  .detail {
    text-align: center;
    
    .value {
      font-size: 0.875rem;
      color: #333;
      font-weight: 500;
      margin-bottom: 4px;
    }
    
    .label {
      font-size: 0.75rem;
      color: #666;
    }
  }
`;

const PropertyPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .price {
    font-size: 1.25rem;
    color: #34574A;
    font-weight: 500;
  }

  .period {
    font-size: 0.875rem;
    color: #666;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const ViewButton = styled.a`
  flex: 1;
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { background: #274137; }
`;

const EditButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
  &:hover { background: #274137; }
`;

const DeleteButton = styled.button`
  background: #B00020;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
  &:hover { background: #7a0014; }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h3 {
    font-size: 1.25rem;
    color: #333;
    margin-bottom: 12px;
  }

  p {
    color: #666;
    margin: 0;
  }
`;

// Troca a URL fixa pela variável de ambiente
const API_URL = `${import.meta.env.VITE_URL_API}/favoritos`;

async function removeFavorito(codigoFAVORITO) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_URL}/${codigoFAVORITO}`, {
    method: 'DELETE',
    headers: {
      'accept': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  if (!res.ok) throw new Error('Erro ao remover favorito');
}

const ModalOverlay = styled.div`
  position: fixed;
  left: 0; top: 0;
  width: 100vw;
  height: 100vh;
  width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.35);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ModalBox = styled.div`
  background: #fff;
  padding: 36px 32px 28px 32px;
  border-radius: 16px;
  min-width: 340px;
  box-shadow: 0 8px 32px rgba(52,87,74,0.18);
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
const ModalTitle = styled.h3`
  margin: 0 0 18px 0;
  font-size: 1.32rem;
  color: #34574A;
  font-weight: 700;
  text-align: center;
`;
const ModalLabel = styled.label`
  font-size: 1rem;
  color: #333;
  margin-bottom: 8px;
  margin-top: 12px;
`;
const ModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid #eee;
  font-size: 1rem;
  margin-top: 2px;
  margin-bottom: 10px;
  &:focus { border-color: #34574A; outline: none; }
`;
const ModalSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid #eee;
  font-size: 1rem;
  margin-top: 2px;
  margin-bottom: 10px;
  &:focus { border-color: #34574A; outline: none; }
`;
const ModalActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 18px;
  justify-content: center;
`;
const ModalButton = styled.button`
  background: ${p=>p.danger ? '#B00020' : '#34574A'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 26px;
  font-size: 1.08rem;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: ${p=>p.danger ? '#7a0014' : '#274137'}; }
`;

const Favorites = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [favoritos, setFavoritos] = useState([]);
  const [imoveis, setImoveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null); // código do favorito a excluir

  // Descobre o usuário logado a partir do localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const id = u?.id || u?.idUSUARIO || u?.codigoUSUARIO || u?.userId || u?.usuarioId || null;
        setUserId(id);
      } else {
        setUserId(null);
      }
    } catch {
      setUserId(null);
    }
  }, []);


  // Carrega favoritos + imóveis populados via novo endpoint
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Usuário não autenticado');
      setFavoritos([]);
      setImoveis([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const resolveUserId = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || '')) || {};
        const fromToken = payload.codigo || payload.id || payload.userId || payload.idUSUARIO || payload.codigoUSUARIO || payload.usuarioId;
        if (fromToken) return fromToken;
      } catch (e) { console.error(e); }
      try {
        const raw = localStorage.getItem('user');
        if (raw) {
          const u = JSON.parse(raw);
          return u?.id || u?.idUSUARIO || u?.codigoUSUARIO || u?.userId || u?.usuarioId || null;
        }
      } catch (e) { console.error(e); }
      return userId;
    };

    const uid = resolveUserId();
    if (!uid) {
      setFavoritos([]);
      setImoveis([]);
      setError('Usuário não autenticado');
      setLoading(false);
      return;
    }

    fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${uid}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Erro ao buscar favoritos');
        const json = await res.json();
        const dataRaw = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        // Mantém apenas favoritos ativos (quando houver flag) e com algum id de imóvel
        const data = dataRaw
          .filter(f => f.ativoFAVORITO === undefined || f.ativoFAVORITO === 1 || f.ativoFAVORITO === true)
          .filter(f => f.imovelFAVORITO || f.codigoIMOVEL || f.idIMOVEL || f.id);
        
        console.log('🔍 DEBUG - Favoritos recebidos do endpoint:', data);
        // Deduplica por imóvel (prioridade) e, se não houver id, usa chave derivada (slug/título/imagem)
        const seen = new Set();
        const favKey = (fav) => {
          const imovelId = fav.imovelFAVORITO || fav.codigoIMOVEL || fav.idIMOVEL || fav.id;
          const favId = fav.codigoFAVORITO || '';
          const slug = fav.slugIMOVEL || fav.slug || '';
          const titulo = fav.tituloIMOVEL || fav.titulo || fav.nomeIMOVEL || '';
          const imagem = fav.imagemIMOVEL || (fav.fotos && fav.fotos[0]?.urlFOTO) || '';
          const composite = `${slug}|${titulo}`.trim();
          return String(imovelId || favId || composite || imagem || 'no-key');
        };
        const deduped = data.filter(fav => {
          const key = favKey(fav);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setFavoritos(deduped);
        // Busca detalhes de cada imóvel favorito (já deduplicado) para preencher campos vazios
        const details = await Promise.allSettled(
          deduped.map((fav) => {
            const id = fav.imovelFAVORITO || fav.codigoIMOVEL || fav.idIMOVEL || fav.id;
            if (!id) return Promise.resolve(null);
            return fetch(`${import.meta.env.VITE_URL_API}/imoveis/${id}`, {
              headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            })
              .then(r => r.ok ? r.json() : null)
              .catch(() => null);
          })
        );
        const imoveisDetalhados = details
          .map(r => (r.status === 'fulfilled' ? r.value : null))
          .filter(Boolean)
          .map(item => Array.isArray(item?.data) ? item.data[0] : (item?.data || item));
        console.log('🔍 DEBUG - Imóveis detalhados recebidos da API:', imoveisDetalhados);
        setImoveis(imoveisDetalhados);
        setLoading(false);
      })
      .catch(() => {
        setFavoritos([]);
        setImoveis([]);
        setError('Erro ao buscar favoritos');
        setLoading(false);
      });
  }, [userId]);

  // Relaciona favorito ao imóvel e prioriza sempre os dados do imóvel real
  const imoveisArr = Array.isArray(imoveis) ? imoveis : (imoveis?.data || imoveis?.imoveis || []);
  const favoritosComImovel = favoritos.map(fav => {
    const favId = fav.imovelFAVORITO || fav.codigoIMOVEL || fav.idIMOVEL || fav.id;
    if (!favId) return null;
    // Tenta usar o imóvel já retornado no próprio favorito ou na lista detalhada
    const imovelInline = fav.imovel || fav.imovelData || fav.imovelFavorito;
    const imovel = imovelInline || imoveisArr.find(i => {
      const iid = i.codigoIMOVEL || i.idIMOVEL || i.id || i.codigo;
      return favId && iid && String(iid) === String(favId);
    });
    const fallback = {
      codigoIMOVEL: favId,
      slugIMOVEL: fav.slugIMOVEL || fav.slug || '',
      dadosBasicos: {
        tituloIMOVEL: fav.tituloIMOVEL || fav.titulo || fav.nomeIMOVEL || '',
        valorIMOVEL: fav.valorIMOVEL || fav.precoIMOVEL || fav.valor || fav.preco || 0,
        periodoIMOVEL: fav.periodoIMOVEL || fav.periodo || null,
      },
      enderecos: {
        enderecoIMOVEL: fav.localizacaoIMOVEL || fav.cidadeIMOVEL || fav.enderecoIMOVEL || '',
      },
      caracteristicas: {
        quartosIMOVEL: fav.quartosIMOVEL || fav.dormitoriosIMOVEL || fav.quartos || '-',
        casadebanhoIMOVEL: fav.banheirosIMOVEL || fav.bathroomsIMOVEL || fav.banheiros || '-',
        vagasgaragemIMOVEL: fav.vagasIMOVEL || fav.vagasGaragemIMOVEL || fav.vagas || '-',
        metragemIMOVEL: fav.areaIMOVEL || fav.metrosIMOVEL || fav.area || '-',
      },
      fotos: fav.fotos || [],
    };

    const chosen = imovel || fallback;
    const chosenId = chosen.codigoIMOVEL || chosen.idIMOVEL || chosen.id || chosen.codigo || favId;
    return {
      ...fav,
      imovel: chosen,
      codigoIMOVEL: chosenId,
      slugIMOVEL: chosen.slugIMOVEL || chosen.slug || fallback.slugIMOVEL || fallback.dadosBasicos?.slugIMOVEL,
      // Sempre sobrescreve os campos de exibição pelos dados disponíveis (considerando estrutura aninhada)
      tituloIMOVEL: chosen.dadosBasicos?.tituloIMOVEL || chosen.tituloIMOVEL || chosen.title || chosen.nome || chosen.nomeIMOVEL || fallback.dadosBasicos?.tituloIMOVEL || 'Imóvel Favorito',
      imagemIMOVEL: (chosen.fotos && chosen.fotos.length > 0) ? (chosen.fotos[0].urlFOTO || chosen.fotos[0].pathFOTO || chosen.fotos[0].url || chosen.fotos[0].path) : (chosen.imagemIMOVEL || chosen.capaIMOVEL || fallback.imagemIMOVEL || ''),
      localizacaoIMOVEL: chosen.enderecos?.enderecoIMOVEL || chosen.enderecoIMOVEL || chosen.localizacaoIMOVEL || chosen.cityIMOVEL || chosen.cidadeIMOVEL || chosen.cidade || fallback.enderecos?.enderecoIMOVEL || '',
      precoIMOVEL: chosen.dadosBasicos?.valorIMOVEL || chosen.valorIMOVEL || chosen.precoIMOVEL || chosen.valor || chosen.preco || fallback.dadosBasicos?.valorIMOVEL || 0,
      periodoIMOVEL: chosen.dadosBasicos?.periodoIMOVEL || chosen.periodoIMOVEL || chosen.periodo || fallback.dadosBasicos?.periodoIMOVEL || null,
    };
  });

  const filteredProperties = favoritosComImovel
    .filter(Boolean)
    // Evita duplicar cards no mapeamento final (chave por imóvel ou slug/título/imagem)
    .filter((fav, idx, arr) => {
      const key = String(fav.codigoIMOVEL || fav.codigoFAVORITO || fav.slugIMOVEL || fav.imagemIMOVEL || fav.tituloIMOVEL || 'no-key');
      return arr.findIndex(f => String(f.codigoIMOVEL || f.codigoFAVORITO || f.slugIMOVEL || f.imagemIMOVEL || f.tituloIMOVEL || 'no-key') === key) === idx;
    })
    .map(fav => {
      const imovel = fav.imovel;
      // Acessa dados considerando estrutura aninhada (dadosBasicos, caracteristicas, enderecos)
      const titulo = imovel?.dadosBasicos?.tituloIMOVEL || imovel?.tituloIMOVEL || imovel?.title || imovel?.nome || fav.tituloIMOVEL || 'Imóvel Favorito';
      const imagem = (imovel?.fotos && imovel.fotos.length > 0)
        ? (imovel.fotos[0].urlFOTO || imovel.fotos[0].pathFOTO || imovel.fotos[0].url || imovel.fotos[0].path)
        : (imovel?.imagemIMOVEL || imovel?.capaIMOVEL || fav.imagemIMOVEL || '/imoveis/01.webp');
      const local = imovel?.enderecos?.enderecoIMOVEL || imovel?.enderecoIMOVEL || imovel?.localizacaoIMOVEL || imovel?.cityIMOVEL || imovel?.cidadeIMOVEL || imovel?.cidade || fav.localizacaoIMOVEL || 'Localização não informada';
      const preco = imovel?.dadosBasicos?.valorIMOVEL || imovel?.valorIMOVEL || imovel?.precoIMOVEL || imovel?.valor || imovel?.preco || fav.precoIMOVEL || fav.valorIMOVEL || 0;
      const quartos = imovel?.caracteristicas?.quartosIMOVEL || imovel?.quartosIMOVEL || imovel?.dormitoriosIMOVEL || imovel?.bedroomsIMOVEL || imovel?.quartos || fav.quartosIMOVEL || '-';
      const banheiros = imovel?.caracteristicas?.casadebanhoIMOVEL || imovel?.caracteristicas?.banheirosIMOVEL || imovel?.banheirosIMOVEL || imovel?.bathroomsIMOVEL || imovel?.banheiros || fav.banheirosIMOVEL || '-';
      const vagas = imovel?.caracteristicas?.vagasgaragemIMOVEL || imovel?.vagasIMOVEL || imovel?.vagasGaragemIMOVEL || imovel?.parkingSpaces || imovel?.vagas || fav.vagasIMOVEL || '-';
      const area = imovel?.caracteristicas?.metragemIMOVEL || imovel?.areaIMOVEL || imovel?.metrosIMOVEL || imovel?.area || fav.areaIMOVEL || '-';
      const periodo = imovel?.dadosBasicos?.periodoIMOVEL || imovel?.periodoIMOVEL || imovel?.periodo || fav.periodoIMOVEL || null;
      
      const mapped = {
        ...imovel,
        codigoFAVORITO: fav.codigoFAVORITO,
        ativoFAVORITO: fav.ativoFAVORITO,
        tituloIMOVEL: titulo,
        imagemIMOVEL: imagem,
        localizacaoIMOVEL: local,
        precoIMOVEL: preco,
        quartosIMOVEL: quartos,
        banheirosIMOVEL: banheiros,
        vagasIMOVEL: vagas,
        areaIMOVEL: area,
        periodoIMOVEL: periodo,
      };
      
      return mapped;
    })
    .filter(property => {
      // Filtro de busca por título ou localização
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (property.tituloIMOVEL && property.tituloIMOVEL.toLowerCase().includes(searchLower)) ||
        (property.localizacaoIMOVEL && property.localizacaoIMOVEL.toLowerCase().includes(searchLower));
      
      return matchesSearch;
    });
    
    // Log para debug dos imóveis mapeados
    console.log('🔍 DEBUG - Imóveis mapeados para exibição:', filteredProperties.slice(0, 2));


  const handleRemoveFavorite = async (codigoFAVORITO) => {
    setShowDeleteModal(codigoFAVORITO);
  };

  const confirmRemoveFavorite = async () => {
    try {
      await removeFavorito(showDeleteModal);
      setFavoritos(favoritos.filter(fav => fav.codigoFAVORITO !== showDeleteModal));
      setShowDeleteModal(null);
    } catch (err) {
      setError('Erro ao remover favorito');
      setShowDeleteModal(null);
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Os meus Favoritos</h1>
        <span className="count">{filteredProperties.length} imóveis</span>
      </Header>
      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Buscar por título ou localização..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>
      {error && <div style={{ color: '#B00020', marginBottom: 18 }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', marginTop: 32 }}>Carregando...</div>
      ) : filteredProperties.length > 0 ? (
        <PropertiesGrid>
          {filteredProperties.map(property => (
            <PropertyCard key={property.codigoFAVORITO}>
              <PropertyImage $image={property.imagemIMOVEL || '/imoveis/01.webp'}>
                <FavoriteButton
                  $active={true}
                  onClick={() => handleRemoveFavorite(property.codigoFAVORITO)}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                </FavoriteButton>
              </PropertyImage>
              <PropertyContent>
                <PropertyTitle>{property.tituloIMOVEL || 'Imóvel Favorito'}</PropertyTitle>
                <PropertyLocation>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  </svg>
                  {property.localizacaoIMOVEL || 'Localização não informada'}
                </PropertyLocation>
                <PropertyDetails>
                  <div className="detail">
                    <div className="value">{property.quartosIMOVEL || '-'}</div>
                    <div className="label">Quartos</div>
                  </div>
                  <div className="detail">
                    <div className="value">{property.banheirosIMOVEL || '-'}</div>
                    <div className="label">Casas de banho</div>
                  </div>
                  <div className="detail">
                    <div className="value">{property.areaIMOVEL || '-'}m²</div>
                    <div className="label">Área</div>
                  </div>
                  <div className="detail">
                    <div className="value">{property.vagasIMOVEL || '-'}</div>
                    <div className="label">{t('propertyCard.parkingLabel')}</div>
                  </div>
                </PropertyDetails>
                <PropertyPrice>
                  <span className="price">
                    {property.precoIMOVEL && property.precoIMOVEL !== 0 
                      ? `€${property.precoIMOVEL}` 
                      : 'Preço indisponível'}
                  </span>
                  {property.periodoIMOVEL && property.precoIMOVEL && property.precoIMOVEL !== 0 && (
                    <span className="period">/{property.periodoIMOVEL}</span>
                  )}
                </PropertyPrice>
                <ActionButtons>
                  <ViewButton href={`/property/${property.codigoIMOVEL}/${property.slugIMOVEL || 'imovel'}`} target="_blank">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    Ver Detalhes
                  </ViewButton>
                </ActionButtons>
              </PropertyContent>
            </PropertyCard>
          ))}
        </PropertiesGrid>
      ) : (
        <NoResults>
          <h3>Nenhum favorito encontrado.</h3>
          <p>Adicione imóveis aos seus favoritos para vê-los aqui.</p>
        </NoResults>
      )}
      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalBox>
            <ModalTitle>Excluir Favorito</ModalTitle>
            <div style={{color:'#B00020',textAlign:'center',marginBottom:18,fontWeight:600}}>Tem certeza que deseja excluir este favorito?</div>
            <ModalActions>
              <ModalButton danger onClick={confirmRemoveFavorite}>Excluir</ModalButton>
              <ModalButton onClick={()=>setShowDeleteModal(null)}>Cancelar</ModalButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Favorites;